import { api } from '../../app/api';

import { serializeOrderForm } from './orders.lib';
import type { Order, OrderForm, Shipment, SplitOrderPayload, SplitOrderResult } from './orders.schema';
import {
    EnabledProvidersResponseSchema,
    OrderResponseSchema,
    OrderSchema,
    ShipmentListResponseSchema,
    SplitOrderResponseSchema,
} from './orders.schema';

// Column + tab filters, merged and sent to /orders as a JSON-stringified
// `extraFilters` param (the model does JSON.parse + a Sequelize where-merge).
export type OrderFilters = {
    status?: string;
    paymentStatus?: string;
    orderType?: string;
    isPreorder?: number | null;
    isBackorder?: number | null;
    isSingles?: number | null;
    delivery?: string[];
    issues?: number;
};

type ListArgs = {
    start?: number;
    limit?: number;
    sort?: string;
    direction?: 'ASC' | 'DESC';
    search?: string;
    filters?: OrderFilters;
};

// Drop null/empty keys before stringify so an unset filter doesn't constrain
// the query (matches the model's `if (value || value === 0)` keep-rule).
function cleanFilters(filters: OrderFilters | undefined): string | undefined {
    if (!filters) return undefined;
    const out: Record<string, unknown> = {};
    for (const [k, v] of Object.entries(filters)) {
        if (v === 0 || (v != null && v !== '' && !(Array.isArray(v) && v.length === 0))) {
            out[k] = v;
        }
    }
    return Object.keys(out).length ? JSON.stringify(out) : undefined;
}

type LooseOption = { id: number; label: string; secondary?: string | null; raw: unknown };

const ordersApi = api.injectEndpoints({
    endpoints: (builder) => ({
        listOrders: builder.query<{ data: Order[]; count: number }, ListArgs | void>({
            query: (args) => {
                const { start = 0, limit = 20, sort = 'id', direction = 'DESC', search, filters } = args ?? {};
                return {
                    url: '/orders',
                    method: 'GET',
                    params: {
                        _start: start,
                        _end: start + limit,
                        _sort: sort,
                        _order: direction,
                        q: search || undefined,
                        extraFilters: cleanFilters(filters),
                    },
                };
            },
            // Parse each row independently — a single malformed row among tens of
            // thousands must not blank the whole list (a strict array parse would
            // throw and surface as "no orders").
            transformResponse: (raw: unknown) => {
                const env = raw as { data?: unknown[]; count?: number };
                const list = Array.isArray(env?.data) ? env.data : [];
                const data: Order[] = [];
                for (const row of list) {
                    const r = OrderSchema.safeParse(row);
                    if (r.success) data.push(r.data);
                    else if (process.env.NODE_ENV !== 'production')
                        // eslint-disable-next-line no-console
                        console.warn('Order row failed validation:', r.error.issues[0]);
                }
                return { data, count: Number(env?.count ?? data.length) };
            },
            providesTags: (result) =>
                result
                    ? [
                          ...result.data.map((o) => ({ type: 'Order' as const, id: o.id })),
                          { type: 'Order' as const, id: 'LIST' },
                      ]
                    : [{ type: 'Order' as const, id: 'LIST' }],
        }),

        getOrder: builder.query<Order, number>({
            query: (id) => ({ url: '/orders/[id]', method: 'GET', query: { id } }),
            transformResponse: (raw: unknown) => OrderResponseSchema.parse(raw).data,
            providesTags: (_r, _e, id) => [{ type: 'Order', id }],
        }),

        createOrder: builder.mutation<Order, OrderForm>({
            query: (values) => ({ url: '/orders', method: 'POST', body: serializeOrderForm(values) }),
            transformResponse: (raw: unknown) => OrderResponseSchema.parse(raw).data,
            invalidatesTags: [{ type: 'Order', id: 'LIST' }],
        }),

        updateOrder: builder.mutation<Order, { id: number; values: OrderForm }>({
            query: ({ id, values }) => ({
                url: '/orders/[id]',
                method: 'PUT',
                query: { id },
                body: serializeOrderForm(values),
            }),
            transformResponse: (raw: unknown) => OrderResponseSchema.parse(raw).data,
            invalidatesTags: (_r, _e, { id }) => [
                { type: 'Order', id },
                { type: 'Order', id: 'LIST' },
            ],
        }),

        deleteOrder: builder.mutation<void, number>({
            query: (id) => ({ url: '/orders/[id]', method: 'DELETE', query: { id } }),
            invalidatesTags: (_r, _e, id) => [
                { type: 'Order', id },
                { type: 'Order', id: 'LIST' },
            ],
        }),

        // status-update returns { success, data, response }; on failure the
        // controller sends 400 with a bare ERR_ string (surfaced by baseQuery).
        updateOrderStatus: builder.mutation<Order, { id: number; status: string; extraData?: unknown }>({
            query: ({ id, status, extraData }) => ({
                url: '/orders/[id]/status-update',
                method: 'PUT',
                query: { id },
                body: { status, extraData },
            }),
            transformResponse: (raw: unknown) => OrderResponseSchema.parse(raw).data,
            invalidatesTags: (_r, _e, { id }) => [
                { type: 'Order', id },
                { type: 'Order', id: 'LIST' },
            ],
        }),

        // Server ignores the body and flips to paid/refunded based on status.
        updatePaymentStatus: builder.mutation<Order, number>({
            query: (id) => ({
                url: '/orders/[id]/payment-status-update',
                method: 'PUT',
                query: { id },
                body: { status: 'confirmed' },
            }),
            transformResponse: (raw: unknown) => OrderResponseSchema.parse(raw).data,
            invalidatesTags: (_r, _e, id) => [
                { type: 'Order', id },
                { type: 'Order', id: 'LIST' },
            ],
        }),

        resendPaymentLink: builder.mutation<unknown, number>({
            query: (id) => ({ url: '/orders/[id]/resend-payment-link', method: 'POST', query: { id }, body: {} }),
        }),

        addActivity: builder.mutation<Order, { id: number; activityType: string; activityData?: unknown }>({
            query: ({ id, activityType, activityData }) => ({
                url: '/orders/[id]/activity',
                method: 'POST',
                query: { id },
                body: { activityType, activityData },
            }),
            transformResponse: (raw: unknown) => OrderResponseSchema.parse(raw).data,
            invalidatesTags: (_r, _e, { id }) => [{ type: 'Order', id }],
        }),

        // merge takes order NUMBERS; returns the raw envelope (may carry {issue}).
        mergeOrders: builder.mutation<{ success?: boolean; issue?: string }, { orderA: string; orderB: string }>({
            query: (body) => ({ url: '/orders/merge', method: 'POST', body }),
            transformResponse: (raw: unknown) => raw as { success?: boolean; issue?: string },
            invalidatesTags: [{ type: 'Order', id: 'LIST' }],
        }),

        // split moves line quantities (keyed by OrderProduct/OrderCard row id)
        // onto a new order; the server also stornoes + reissues invoices.
        splitOrder: builder.mutation<SplitOrderResult, { id: number; body: SplitOrderPayload }>({
            query: ({ id, body }) => ({ url: '/v2/orders/[id]/split', method: 'POST', query: { id }, body }),
            transformResponse: (raw: unknown) => SplitOrderResponseSchema.parse(raw).data,
            invalidatesTags: (_r, _e, { id }) => [
                { type: 'Order', id },
                { type: 'Order', id: 'LIST' },
            ],
        }),

        // ---- Imperative document downloads (data URLs / blob). Triggered on
        // click; not cached. ----
        createInvoice: builder.mutation<string, number>({
            query: (id) => ({ url: '/orders/[id]/invoice', method: 'GET', query: { id } }),
            transformResponse: (raw: unknown) => (raw as { data: string }).data,
            invalidatesTags: (_r, _e, id) => [{ type: 'Order', id }],
        }),
        getInvoice: builder.mutation<string, { id: number; invoiceId: number }>({
            query: ({ id, invoiceId }) => ({
                url: '/orders/[id]/invoice/[invoiceId]',
                method: 'GET',
                query: { id, invoiceId },
            }),
            transformResponse: (raw: unknown) => (raw as { data: string }).data,
        }),
        regenerateInvoice: builder.mutation<string, { id: number; invoiceId: number }>({
            query: ({ id, invoiceId }) => ({
                url: '/orders/[id]/invoice/[invoiceId]/regenerate',
                method: 'GET',
                query: { id, invoiceId },
            }),
            transformResponse: (raw: unknown) => (raw as { data: string }).data,
            invalidatesTags: (_r, _e, { id }) => [{ type: 'Order', id }],
        }),
        getAwb: builder.mutation<string, { id: number; format: 'A4' | 'A6' }>({
            query: ({ id, format }) => ({
                url: '/orders/[id]/awb',
                method: 'GET',
                query: { id },
                params: { format },
            }),
            transformResponse: (raw: unknown) => (raw as { data: string }).data,
        }),
        getQr: builder.mutation<string, number>({
            query: (id) => ({ url: '/orders/[id]/qr', method: 'GET', query: { id } }),
            transformResponse: (raw: unknown) => (raw as { data: string }).data,
        }),
        getPrintout: builder.mutation<Blob, number>({
            query: (id) => ({
                url: '/orders/[id]/printout',
                method: 'GET',
                query: { id },
                opts: { responseType: 'blob' },
            }),
            transformResponse: (raw: unknown) => raw as Blob,
        }),

        // ---- Interval exports (IntervalExportModal). Each returns the raw
        // envelope; the modal triggers the download. ----
        exportReport: builder.mutation<unknown, { url: string; start: string; end: string }>({
            query: ({ url, start, end }) => ({ url, method: 'GET', params: { start, end } }),
        }),

        // ---- Shipments (v2 module) ----
        listShipments: builder.query<Shipment[], number>({
            query: (orderId) => ({ url: '/v2/orders/[orderId]/shipments', method: 'GET', query: { orderId } }),
            transformResponse: (raw: unknown) => ShipmentListResponseSchema.parse(raw).data,
            providesTags: (_r, _e, orderId) => [{ type: 'OrderShipment', id: `LIST-${orderId}` }],
        }),
        createExtraShipment: builder.mutation<unknown, { orderId: number; body: unknown }>({
            query: ({ orderId, body }) => ({
                url: '/v2/orders/[orderId]/shipments/extra',
                method: 'POST',
                query: { orderId },
                body,
            }),
            invalidatesTags: (_r, _e, { orderId }) => [
                { type: 'OrderShipment', id: `LIST-${orderId}` },
                { type: 'Order', id: orderId },
            ],
        }),
        createExchangeShipment: builder.mutation<unknown, { orderId: number; body: unknown }>({
            query: ({ orderId, body }) => ({
                url: '/v2/orders/[orderId]/shipments/exchange',
                method: 'POST',
                query: { orderId },
                body,
            }),
            invalidatesTags: (_r, _e, { orderId }) => [
                { type: 'OrderShipment', id: `LIST-${orderId}` },
                { type: 'Order', id: orderId },
            ],
        }),
        cancelShipment: builder.mutation<unknown, { id: number; orderId: number }>({
            query: ({ id }) => ({ url: '/v2/order-shipments/[id]/cancel', method: 'POST', query: { id }, body: {} }),
            invalidatesTags: (_r, _e, { orderId }) => [{ type: 'OrderShipment', id: `LIST-${orderId}` }],
        }),
        downloadShipmentLabel: builder.mutation<string, { id: number; format: 'A4' | 'A6' }>({
            query: ({ id, format }) => ({
                url: '/v2/order-shipments/[id]/label',
                method: 'GET',
                query: { id },
                params: { format },
            }),
            transformResponse: (raw: unknown) => (raw as { data: string }).data,
        }),
        enabledProviders: builder.query<{ id: string; label: string }[], void>({
            query: () => ({ url: '/v2/shipping/enabled-providers', method: 'GET' }),
            transformResponse: (raw: unknown) => {
                const parsed = EnabledProvidersResponseSchema.parse(raw);
                const list = Array.isArray(parsed) ? parsed : parsed.data;
                return list.map((p) => ({ id: p.id, label: p.label ?? p.id }));
            },
        }),

        // ---- Autocomplete sources (legacy endpoints, loose shapes) ----
        searchProducts: builder.query<LooseOption[], string>({
            query: (q) => ({ url: '/products', method: 'GET', params: { _start: 0, _end: 25, q: q || undefined } }),
            transformResponse: (raw: unknown) => toProductOptions(raw),
        }),
        searchCards: builder.query<LooseOption[], string>({
            query: (q) => ({ url: '/cards', method: 'GET', params: { _start: 0, _end: 25, q: q || undefined } }),
            transformResponse: (raw: unknown) => toCardOptions(raw),
        }),
        searchCounties: builder.query<{ id: number; name: string }[], string>({
            query: (name) => ({ url: '/public/county-autocomplete', method: 'GET', params: { name: name || undefined } }),
            // Body shape: { data: { counties: [{ id, name }] } }
            transformResponse: (raw: unknown) => {
                const counties = ((raw as { data?: { counties?: unknown[] } })?.data?.counties) ?? [];
                return counties.map((r) => {
                    const o = r as { id: number; name?: string };
                    return { id: Number(o.id), name: o.name ?? `#${o.id}` };
                });
            },
        }),
        searchCities: builder.query<{ id: number; name: string; county?: string }[], { name: string; county?: number }>({
            query: ({ name, county }) => ({
                url: '/public/city-autocomplete',
                method: 'GET',
                params: { name: name || undefined, county },
            }),
            // Body shape: { data: { cities: [{ id, name, county: { name } }] } }
            transformResponse: (raw: unknown) => {
                const cities = ((raw as { data?: { cities?: unknown[] } })?.data?.cities) ?? [];
                return cities.map((r) => {
                    const o = r as { id: number; name?: string; county?: { name?: string } };
                    return { id: Number(o.id), name: o.name ?? `#${o.id}`, county: o.county?.name };
                });
            },
        }),
        listPaymentTypes: builder.query<{ id: number; name: string; handler?: string }[], void>({
            query: () => ({ url: '/payment-types', method: 'GET', params: { _start: 0, _end: 100 } }),
            transformResponse: (raw: unknown) => {
                const list = unwrapList(raw);
                return list.map((r) => {
                    const o = r as { id: number; name?: string; handler?: string };
                    return { id: Number(o.id), name: o.name ?? `#${o.id}`, handler: o.handler };
                });
            },
        }),
    }),
});

// ---- transformResponse helpers (legacy endpoints have no zod schema) ----
function unwrapList(raw: unknown): unknown[] {
    if (Array.isArray(raw)) return raw;
    const d = (raw as { data?: unknown })?.data;
    return Array.isArray(d) ? d : [];
}

function toProductOptions(raw: unknown): LooseOption[] {
    return unwrapList(raw).map((row) => {
        const r = row as { id: number; title?: string; sku?: string };
        return { id: Number(r.id), label: r.title ?? `Produs #${r.id}`, secondary: r.sku ?? null, raw: row };
    });
}

function toCardOptions(raw: unknown): LooseOption[] {
    return unwrapList(raw).map((row) => {
        const r = row as { id: number; name?: string; code?: string; variantName?: string };
        return {
            id: Number(r.id),
            label: r.name ?? `Carte #${r.id}`,
            secondary: [r.code, r.variantName ? `[${r.variantName}]` : null].filter(Boolean).join(' ') || null,
            raw: row,
        };
    });
}

export const {
    useListOrdersQuery,
    useGetOrderQuery,
    useCreateOrderMutation,
    useUpdateOrderMutation,
    useDeleteOrderMutation,
    useUpdateOrderStatusMutation,
    useUpdatePaymentStatusMutation,
    useResendPaymentLinkMutation,
    useAddActivityMutation,
    useMergeOrdersMutation,
    useSplitOrderMutation,
    useCreateInvoiceMutation,
    useGetInvoiceMutation,
    useRegenerateInvoiceMutation,
    useGetAwbMutation,
    useGetQrMutation,
    useGetPrintoutMutation,
    useExportReportMutation,
    useListShipmentsQuery,
    useCreateExtraShipmentMutation,
    useCreateExchangeShipmentMutation,
    useCancelShipmentMutation,
    useDownloadShipmentLabelMutation,
    useEnabledProvidersQuery,
    useSearchProductsQuery,
    useSearchCardsQuery,
    useSearchCountiesQuery,
    useSearchCitiesQuery,
    useListPaymentTypesQuery,
} = ordersApi;
